import { useEffect, useRef, useState } from "react";
import { Search, SendHorizonal, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import MicButton from "@/components/ui/MicButton";
import { fetchInvestigation, resetInvestigation, streamInvestigation } from "@/lib/ia";
import IaMessage from "@/components/ia/IaMessage";

const SUGGESTIONS = [
    "Quels sont les 5 plus gros postes de charges dans le FEC et leur évolution ?",
    "Y a-t-il des écritures inhabituelles ou des doublons dans le FEC ?",
    "Détaille la structure de l'endettement à partir de la liasse.",
];

// LOT 33 : chat d'investigation PUR — l'IA répond simplement, sans génération de Mémo Flash ni intégration/épinglage.
export default function InvestigationChat({ dossierId }) {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [streaming, setStreaming] = useState(false);
    const [status, setStatus] = useState("");
    const endRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        fetchInvestigation(dossierId)
            .then((s) => { if (!cancelled) setMessages(s.historique); })
            .catch((e) => { if (!cancelled) toast.error("Chat d'investigation", { description: e.message }); });
        return () => { cancelled = true; };
    }, [dossierId]);

    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, [messages]);

    const locked = streaming;

    async function send(q) {
        if (!q || locked) return;
        setInput("");
        setStreaming(true);
        setStatus("preparation");
        setMessages((ms) => [...ms, { role: "user", content: q, kind: "investigation" }, { role: "assistant", content: "", kind: "investigation" }]);
        try {
            await streamInvestigation(dossierId, { message: q }, {
                onStatus: setStatus,
                onDelta: (d) => setMessages((ms) => [...ms.slice(0, -1), { ...ms[ms.length - 1], content: ms[ms.length - 1].content + d }]),
                onDone: (ev) => setMessages((ms) => ms.map((m, i) => (i === ms.length - 1 ? { ...m, sources: ev.sources || [] } : m))),
                onError: (err) => { toast.error("Erreur IA", { description: err }); setMessages((ms) => ms.slice(0, -2)); },
            });
        } catch (e) {
            toast.error("Connexion interrompue", { description: e.message });
            setMessages((ms) => (ms[ms.length - 1]?.content ? ms : ms.slice(0, -2)));
        } finally {
            setStreaming(false);
        }
    }

    async function reset() {
        if (!window.confirm("Effacer toute la conversation d'investigation ?")) return;
        try {
            await resetInvestigation(dossierId);
            setMessages([]);
        } catch (e) {
            toast.error(e.message);
        }
    }

    return (
        <section data-testid="investigation-chat" className="space-y-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-accent flex items-center justify-center">
                        <Search className="h-4 w-4 text-primary" strokeWidth={1.75} />
                    </div>
                    <div>
                        <h3 className="font-display text-xl text-foreground">Chat d&apos;investigation IA</h3>
                        <p className="text-xs text-muted-foreground">Posez vos questions : l&apos;IA a accès aux données Pappers, aux KPI et au contenu brut de la dataroom (FEC, liasses). Réponses directes, sans génération de rapport.</p>
                    </div>
                </div>
                {messages.length > 0 && (
                    <Button variant="ghost" size="sm" data-testid="investigation-reset-button" onClick={reset} disabled={locked} className="rounded-full text-muted-foreground">
                        <RotateCcw className="h-4 w-4" strokeWidth={1.75} /> Effacer
                    </Button>
                )}
            </div>

            {messages.length === 0 ? (
                <div data-testid="investigation-empty" className="space-y-3">
                    <p className="text-sm text-muted-foreground">Aucune question posée. Suggestions :</p>
                    <div className="flex flex-wrap gap-2">
                        {SUGGESTIONS.map((s, i) => (
                            <button key={s} type="button" data-testid={`investigation-suggestion-${i}`} onClick={() => send(s)} disabled={locked}
                                className="rounded-full border border-border bg-card px-3 py-1.5 text-xs text-foreground hover:bg-accent transition-colors">
                                {s}
                            </button>
                        ))}
                    </div>
                </div>
            ) : (
                <div data-testid="investigation-messages" className="space-y-3 max-h-[560px] overflow-y-auto pr-1">
                    {messages.map((m, i) => (
                        <IaMessage key={m.at || `msg-${i}`} message={m} streaming={streaming && i === messages.length - 1} />
                    ))}
                    <div ref={endRef} />
                </div>
            )}

            <form onSubmit={(e) => { e.preventDefault(); send(input.trim()); }} data-testid="investigation-form" className="flex items-end gap-3">
                <Textarea
                    data-testid="investigation-input"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input.trim()); } }}
                    placeholder={streaming ? (status === "generation" ? "Rédaction…" : "Lecture du dossier…") : "Poser une question d'investigation (FEC, liasse, Pappers…) — Entrée pour envoyer"}
                    rows={2}
                    disabled={locked}
                    className="bg-card resize-none"
                />
                <MicButton testId="investigation-mic" disabled={locked} onText={(t) => setInput((v) => (v ? v + " " : "") + t)} />
                <Button type="submit" data-testid="investigation-send" disabled={locked || !input.trim()} className="h-10 w-10 p-0 rounded-full bg-primary hover:bg-primary/90">
                    <SendHorizonal className="h-4 w-4" strokeWidth={2} />
                </Button>
            </form>
        </section>
    );
}
